/**
 * <ta-zellij-families>, grouped zellige collection grid (sections/ta-zellij-families.liquid).
 * Colour dots swap a card's picture, name, price and links in place; colour chips filter the
 * cards by colour group and flip each family card to its matching colour. All data is
 * server-rendered on the dots, so this file holds behaviour only.
 */
class TaZellijFamilies extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.addEventListener('mouseover', this.onHover);
    }
    const wanted = new URLSearchParams(window.location.search).get('colour');
    if (wanted && this.querySelector(`[data-ta-chip="${CSS.escape(wanted)}"]`)) this.setMood(wanted, false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('mouseover', this.onHover);
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const dot = target?.closest('[data-ta-dot]');
    if (dot) {
      if (event.metaKey || event.ctrlKey || event.shiftKey) return; // let people open a colour in a new tab
      event.preventDefault();
      this.activate(dot);
      return;
    }
    const chip = target?.closest('[data-ta-chip]');
    if (chip) this.setMood(chip.dataset.taChip, true);
  };

  /** @param {MouseEvent} event */
  onHover = (event) => {
    const dot = event.target instanceof Element ? event.target.closest('[data-ta-dot]') : null;
    if (dot && dot.getAttribute('aria-pressed') !== 'true') this.activate(dot);
  };

  /** @param {Element} dot */
  activate(dot) {
    const card = dot.closest('[data-ta-card]');
    if (!card) return;
    card.querySelectorAll('[data-ta-dot]').forEach((d) => d.setAttribute('aria-pressed', String(d === dot)));
    card.querySelectorAll('[data-ta-link]').forEach((a) => {
      a.setAttribute('href', dot.getAttribute('href'));
      if (a.hasAttribute('aria-label')) a.setAttribute('aria-label', dot.dataset.name);
    });
    const name = card.querySelector('[data-ta-name]');
    const price = card.querySelector('[data-ta-price]');
    if (name) name.textContent = dot.dataset.name;
    if (price) price.textContent = dot.dataset.price;

    const img = card.querySelector('[data-ta-img]');
    if (!(img instanceof HTMLImageElement) || img.getAttribute('src') === dot.dataset.src) return;
    // load the next picture off-screen first, so the card never flashes empty
    card.dataset.taWant = dot.dataset.src;
    img.classList.add('is-swapping');
    const next = new Image();
    next.sizes = img.sizes;
    next.srcset = dot.dataset.srcset;
    next.src = dot.dataset.src;
    const swap = () => {
      if (card.dataset.taWant !== dot.dataset.src) return; // a later tap won
      img.srcset = dot.dataset.srcset;
      img.src = dot.dataset.src;
      img.alt = dot.dataset.alt;
      img.classList.remove('is-swapping');
    };
    next.decode ? next.decode().then(swap, swap) : (next.onload = swap);
  }

  /**
   * @param {'labelDesigns' | 'labelColourways'} key  dataset holds "singular|plural"
   * @param {number} n
   */
  label(key, n) {
    const [one, many] = (this.dataset[key] || '').split('|');
    return n === 1 ? one : many || one;
  }

  /**
   * @param {string} mood  'all' or a colour group key
   * @param {boolean} pushUrl
   */
  setMood(mood, pushUrl) {
    this.querySelectorAll('[data-ta-chip]').forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.taChip === mood)));
    let cards = 0;
    let colours = 0;
    this.querySelectorAll('[data-ta-card]').forEach((card) => {
      const moods = (card.dataset.moods || '').split(' ');
      const show = mood === 'all' || moods.includes(mood);
      card.hidden = !show;
      if (!show) return;
      cards += 1;
      colours += mood === 'all' ? Math.max(moods.length, 1) : moods.filter((m) => m === mood).length;
      const dots = [...card.querySelectorAll('[data-ta-dot]')];
      if (!dots.length) return;
      const pick = mood === 'all' ? dots[0] : dots.find((d) => d.dataset.mood === mood);
      if (pick) this.activate(pick);
    });
    const count = this.querySelector('[data-ta-count]');
    if (count) count.textContent = `${cards} ${this.label('labelDesigns', cards)} · ${colours} ${this.label('labelColourways', colours)}`;
    // centre the chosen chip in its swipeable row (horizontal only, the page itself never jumps)
    const chip = this.querySelector('[data-ta-chip][aria-pressed="true"]');
    const row = chip?.parentElement;
    if (chip instanceof HTMLElement && row) row.scrollTo({ left: chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2, behavior: 'smooth' });
    if (pushUrl) {
      const url = new URL(window.location.href);
      mood === 'all' ? url.searchParams.delete('colour') : url.searchParams.set('colour', mood);
      window.history.replaceState({}, '', url);
    }
  }
}

if (!customElements.get('ta-zellij-families')) customElements.define('ta-zellij-families', TaZellijFamilies);
